import React, { useState, useRef, useEffect } from "react";
import {
  Container, TextField, IconButton, Typography, Autocomplete, Box, Grid, Button, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, Divider, FormControl, InputLabel, Select, MenuItem,
  Alert, Stepper, Step, StepLabel, Tooltip, FormHelperText
} from "@mui/material";
import SignatureCanvas from 'react-signature-canvas';
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { format } from 'date-fns';
import ShareIcon from '@mui/icons-material/Share';
import LocalHospital from '@mui/icons-material/LocalHospital';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { API_BASE_URL } from '../../lib/constants';
import theme from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, hasDraft } from '../../utils/draftUtils';
import VoiceTextField from "../../components/VoiceTextField";
import { syncPatientData, getSyncedPatientData, SURGERY_TEMPLATES } from "../../utils/formSync";
const ConsentForm = () => {
  const { showNotification } = useNotification();
  const uhid_no = useParams().uhid_no;
  const navigate = useNavigate();
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const operationProcedureOptions = [
    "Appendectomy",
    "Cholecystectomy",
    "Hernia Repair",
    "Coronary Artery Bypass",
    "Knee Replacement",
    "Cataract Surgery",
    "Hip Replacement",
    "Tonsillectomy",
    "Gallbladder Removal",
    "Carpal Tunnel Release",
    "Spinal Fusion",
    "Heart Valve Repair",
    "Mastectomy",
    "Thyroidectomy",
    "Cesarean Section",
    "Prostatectomy",
    "Hysterectomy",
    "Laparoscopy",
    "Angioplasty",
    "Pacemaker Implantation",
    "Nephrectomy",
    "Colectomy",
    "Bariatric Surgery",
    "Liver Transplant",
    "Kidney Transplant"
  ];
  const unableConsentReasons = [
    "Patient unconscious",
    "Patient sedated",
    "Cognitive impairment",
    "Severe pain or distress",
    "Emergency situation",
    "Language barrier",
    "Hearing impairment",
    "Developmental disability",
    "Under influence of medication or substances",
  ];
  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    operation_procedure: "",
    advised_of_major_risk: "",
    care_plan: "",
    care_plan_dr: "",
    care_plan_options: "",
    care_plan_risks: "",
    care_plan_benefits: "",
    authorize_dr: "",
    authorize_by: "",
    diagnosis: "",
    diagnosis_dr: "",
    diagnosis_like: "",
    condition_like: "",
    understand_by_dr: "",
    i_understood_by_dr: "",
    patient_signature: "",
    patient_name: "",
    patient_signature_date_time: "",
    witness_signature: "",
    witness_name: "",
    witness_signature_date_time: "",
    doctor_anaesthetist_signature: "",
    doctor_anaesthetist_name: "",
    doctor_anaesthetist_signature_date_time: "",
    interpreter_signature: "",
    interpreter_name: "",
    interpreter_signature_date_time: "",
    unable_consent_reason: "",
    attender_name: "",
    patient_rep_signature: "",
    patient_rep_name: "",
    patient_rep_signature_date_time: "",
    patient_rep_witness_signature: "",
    patient_rep_witness_name: "",
    patient_rep_witness_date_time: "",
    patient_rep_doctor_signature: "",
    patient_rep_doctor_name: "",
    patient_rep_doctor_signature_date_time: "",
    patient_rep_interpreter_signature: "",
    patient_rep_interpreter_name: "",
    patient_rep_interpreter_signature_date_time: ""
  });

  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Basic Info', 'Procedure Details', 'Risks & Benefits', 'Signatures'];

  // Productivity: Auto-Sync Cross-Form Data
  useEffect(() => {
    if (uhid_no) {
      const syncedData = getSyncedPatientData(uhid_no);
      if (Object.keys(syncedData).length > 0) {
        setFormData(prev => {
          // Only sync if current values are empty to avoid overwriting user input
          const updated = { ...prev };
          if (!prev.diagnosis) updated.diagnosis = syncedData.diagnosis || "";
          if (!prev.operation_procedure) updated.operation_procedure = syncedData.operation_procedure || "";
          if (!prev.ip_no) updated.ip_no = syncedData.ip_no || "";
          return updated;
        });
        showNotification('info', 'Smart Prefill: Loaded data from other forms.');
      }
    }
  }, [uhid_no]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleStepBack = () => setActiveStep((prev) => prev - 1);

  // Productivity: Apply Templates
  const applySurgeryTemplate = (procedure) => {
    const template = SURGERY_TEMPLATES[procedure];
    if (template) {
      setFormData(prev => ({
        ...prev,
        care_plan_risks: template.complications,
        care_plan_benefits: "Better recovery, pain relief.",
        advised_of_major_risk: template.risks
      }));
      showNotification('success', `Applied surgery template for ${procedure}`);
    }
  };
  const [errors, setErrors] = useState({});


  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`consent_medicalForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`consent_medicalForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`consent_medicalForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_consent_medical_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_consent_medical_${uhid_no}`, JSON.stringify({
        formName: 'CONSENT FOR MEDICAL / SURGICAL PROCEDURE',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/consentForMedical/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleBack = () => {
    navigate(`/mainPage/${formData.uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    })
  };
  const ConsentFormSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      const firstErrorField = Object.keys(errors)[0];
      const errorMessage = errors[firstErrorField] || "Please check the form for errors.";
      showNotification('error', `Required Field: ${errorMessage}`);
      return;
    }

    const consentFormData = {
      ip_no: formData.ip_no,
      uhid_no: formData.uhid_no,
      hospital_id: 1,
      operation_procedure: formData.operation_procedure,
      advised_of_major_risk: formData.advised_of_major_risk,
      care_plan: formData.care_plan,
      care_plan_dr: formData.care_plan_dr,
      care_plan_options: formData.care_plan_options,
      care_plan_risks: formData.care_plan_risks,
      care_plan_benefits: formData.care_plan_benefits,
      authorize_dr: formData.authorize_dr,
      authorize_by: formData.authorize_by,
      diagnosis: formData.diagnosis,
      diagnosis_dr: formData.diagnosis_dr,
      diagnosis_like: formData.diagnosis_like,
      condition_like: formData.condition_like,
      understand_by_dr: formData.understand_by_dr,
      i_understood_by_dr: formData.i_understood_by_dr,
      patient_signature: formData.patient_signature,
      patient_name: formData.patient_name,
      patient_signature_date_time: formData.patient_signature_date_time,
      witness_signature: formData.witness_signature,
      witness_name: formData.witness_name,
      witness_signature_date_time: formData.witness_signature_date_time,
      doctor_anaesthetist_signature: formData.doctor_anaesthetist_signature,
      doctor_anaesthetist_name: formData.doctor_anaesthetist_name,
      doctor_anaesthetist_signature_date_time: formData.doctor_anaesthetist_signature_date_time,
      interpreter_signature: formData.interpreter_signature,
      interpreter_name: formData.interpreter_name,
      interpreter_signature_date_time: formData.interpreter_signature_date_time,
      unable_consent_reason: formData.unable_consent_reason,
      attender_name: formData.attender_name,
      patient_rep_signature: formData.patient_rep_signature,
      patient_rep_name: formData.patient_rep_name,
      patient_rep_signature_date_time: formData.patient_rep_signature_date_time,
      patient_rep_witness_signature: formData.patient_rep_witness_signature,
      patient_rep_witness_name: formData.patient_rep_witness_name,
      patient_rep_witness_date_time: formData.patient_rep_witness_date_time,
      patient_rep_doctor_signature: formData.patient_rep_doctor_signature,
      patient_rep_doctor_name: formData.patient_rep_doctor_name,
      patient_rep_doctor_signature_date_time: formData.patient_rep_doctor_signature_date_time,
      patient_rep_interpreter_signature: formData.patient_rep_interpreter_signature,
      patient_rep_interpreter_name: formData.patient_rep_interpreter_name,
      patient_rep_interpreter_signature_date_time: formData.patient_rep_interpreter_signature_date_time,
    }
    console.log(consentFormData)
    try {
      if (success === "success") {
        const responseUpdate = await axios.put(`${API_BASE_URL}/update_medical_consent/${uhid_no}`, consentFormData, { withCredentials: true });
        //navigate(`/preOperative/${uhid_no}`); 

        showNotification('success', 'Medical/Surgical Consent Form updated successfully!');
        navigate(`/mainPage/${formData.uhid_no}`, {
          state: { from: 'PreOperativePhase', status: 'success', step: 1 },
        });
      } else {
        const response = await axios.post(`${API_BASE_URL}/submit_medical_consent`, consentFormData, { withCredentials: true });
        console.log('concentForMedical Response: ', response.data);
        //navigate(`/intraOperative/${uhid_no}`); 

        showNotification('success', 'Medical/Surgical Consent Form submitted successfully!');
      }
      clearSavedData();
      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    } catch (error) {
      console.error('There was an error submitting the form!', error);
      showNotification('error', 'Error in submitting Medical/Surgical Consent Form!');
    }
  };
  const diagnosisTests = ['Blood test', 'MRI scan', 'CT scan', 'X-ray', 'ECG', 'Ultrasound', 'Biopsy', 'Urinalysis'];
  const diagnoses = ['Hypertension', 'Diabetes mellitus', 'Pneumonia', 'Stroke', 'Chronic Kidney Disease', 'COVID-19'];
  const conditionOptions = [
    'Improvement in breathing',
    'Worsening of fever',
    'Increased pain',
    'Seizure',
    'Blood pressure fluctuation',
    'Reduced mobility',
    'Loss of consciousness',
    'Stable vitals',
  ];
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [ipRes] = await Promise.all([

          axios.get(`${API_BASE_URL}/medical_next_ip`)
        ]);
        setFormData(prev => ({
          ...prev,

          ip_no: ipRes.data.next_ip_no
        }));
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, []);
  const doctors = ['Dr. A. Kumar', 'Dr. R. Mehta', 'Dr. S. Sharma', 'Dr. L. Fernandes', 'Dr. P. Iyer'];
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState('');
  const signaturePadRef = useRef(null);
  const signatureDatetimeMap = {
    patient_signature: "patient_signature_date_time",
    witness_signature: "witness_signature_date_time",
    doctor_anaesthetist_signature: "doctor_anaesthetist_signature_date_time",
    interpreter_signature: "interpreter_signature_date_time",

    patient_rep_signature: "patient_rep_signature_date_time",
    patient_rep_witness_signature: "patient_rep_witness_date_time",
    patient_rep_doctor_signature: "patient_rep_doctor_signature_date_time",
    patient_rep_interpreter_signature: "patient_rep_interpreter_signature_date_time"
  };
  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);

      console.log(response.data);
      setFormData(prev => ({
        ...prev,

        name: response.data.name,


      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_medical_consent_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/consentForMedical/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };
  const handleSignatureOpen = (signatureKey) => {
    setCurrentSignatureRole(signatureKey); // Make sure you store this for saving later

    const datetimeKey = signatureDatetimeMap[signatureKey];
    if (datetimeKey) {
      const now = new Date().toISOString().slice(0, 16); // Format for datetime-local input
      setFormData((prev) => ({
        ...prev,
        [datetimeKey]: now
      }));
    }

    setSignatureDialogOpen(true);
  };


  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };
  const handleDialogClose = () => {
    setSignatureDialogOpen(false);
  };
  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();

    // Removed undefined 'imageToBase64' function call
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(',')[1],
    }));
    setSignatureDialogOpen(false);
  };

  const handleChange = (e) => {
    // Fallback if e.target is undefined (like in Autocomplete onInputChange)
    const name = e?.target?.name;
    const value = e?.target?.value;

    if (!name) return; // Exit if name is missing to avoid crashing

    // Validate only for name fields
    const nameFields = [
      "patient_name",
      "acknowledge_patient_name",
      "attender_name",
      "patient_signature_name",
      "doctor_anaesthetist_name",
      "interpreter_name",
      "witness_name",
      "patient_rep_name",
      "patient_rep_doctor_name",
      "patient_rep_interpreter_name",
      "patient_rep_witness_name"
    ];

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Productivity: Sync key fields to cross-form storage
    const syncableFields = ['diagnosis', 'operation_procedure', 'ip_no'];
    if (syncableFields.includes(name)) {
      syncPatientData(uhid_no, { [name]: value });
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";

    switch (name) {
      case 'operation_procedure':
        if (!value || !value.trim()) error = "Procedure is required";
        break;
      case 'advised_of_major_risk':
        if (!value) error = "Major risks must be advised";
        break;
      case 'patient_name':
        if (!value || !value.trim()) error = "Patient name is required";
        break;
      case 'diagnosis':
        if (!value || !value.trim()) error = "Diagnosis is required";
        break;
      // Add other specific field validations as needed
      default:
        break;
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const validate = () => {
    const requiredFields = [
      { key: 'operation_procedure',   label: 'Operation / Procedure'  },
      { key: 'patient_name',          label: 'Patient Name'           },
      { key: 'advised_of_major_risk', label: 'Advised of Major Risk'  },
    ];
    const tempErrors = {};
    requiredFields.forEach(({ key, label }) => {
      if (!formData[key] || String(formData[key]).trim() === '')
        tempErrors[key] = `${label} is required`;
    });
    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) {
      const firstKey = Object.keys(tempErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      showNotification('error', requiredFields.filter(f => tempErrors[f.key]).map(f => f.label));
    }
    return Object.keys(tempErrors).length === 0;
  };
  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/; // allow letters, spaces, dot, hyphen
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setFormData({ ...formData, [e.target.name]: value });
    }
  };

  const carePlanOptionsList = [
    "Surgery",
    "Medication",
    "Physical therapy",
    "Observation",
    "Discharge with follow-up",
    "Palliative care",
  ];

  const carePlanOptions = ["Pre-operative care", "Post-operative care", "Intensive monitoring"];
  const doctorNames = ["Dr. Sharma", "Dr. Patel", "Dr. Mehta"];
  const riskOptions = ["Bleeding", "Infection", "Allergic reaction"];
  const benefitOptions = ["Pain relief", "Improved mobility", "Life-saving intervention"];
  const majorRiskOptions = [
    "Bleeding",
    "Infection",
    "Blood clots",
    "Organ damage",
    "Anesthesia complications",
    "Nerve damage",
    "Heart attack",
    "Stroke",
    "Allergic reaction",
    "Death",
    "Pneumonia",
    "Sepsis",
    "Pulmonary embolism",
    "Deep vein thrombosis",
    "Wound dehiscence",
    "Respiratory failure",
    "Myocardial infarction",
    "Hemorrhage",
    "Renal failure",
    "Shock",
    "Hypoxia",
    "Aspiration",
    "Edema",
    "Arrhythmia",
    "Neurological deficit",
    "Thrombophlebitis",
    "Postoperative delirium",
    "Surgical site infection",
    "Acute respiratory distress syndrome",
    "Cardiac arrest",
    "Hypotension",
    "Hyperthermia",
    "Electrolyte imbalance",
    "Gastrointestinal bleeding",
    "Urinary tract infection",
    "Anaphylaxis",
  ];
  const authorizeOptions = [
    'Dr. A. Kumar',
    'Medical Superintendent',
    'Ethics Committee',
    'Hospital Director',
    'Dr. R. Mehta',
  ];
  const underlineInputStyle = {
    display: 'inline-block',
    minWidth: 150,
    mx: 1, // margin left and right
    '& .MuiInputBase-root': {
      borderBottom: '1px solid black',
    },
    '& input': {
      padding: '4px 0',
      textAlign: 'center',
    }
  };
  useEffect(() => {


    axios.get(`${API_BASE_URL}/get_medical_consent/${uhid_no}`)
      .then((response) => {
        const dateFields = [
          "doctor_anaesthetist_signature_date_time",
          "doctor_signature_unable_consent_date_time",
          "witness_signature_date_time",
          "patient_signature_date_time",
          "interpreter_signature_date_time",
          "interpreter_signature_unable_consent_date_time",
          "patient_rep_signature_date_time",
          "patient_rep_witness_date_time",
          "patient_rep_doctor_signature_date_time",
          "patient_rep_interpreter_signature_date_time",
        ];

        // Format each date field and update the formData
        const formattedData = dateFields.reduce((acc, field) => {
          const raw = response.data[field];
          if (raw) {
            const date = new Date(raw);
            if (!isNaN(date.getTime())) {
              acc[field] = date.toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:MM'
            } else {
              console.warn(`Invalid date: ${field} =`, raw);
            }
          }
          return acc;
        }, {});




        // Convert all signatures to base64
        const signatureFields = [
          "patient_signature",
          "doctor_anaesthetist_signature",
          "doctor_signature_unable_consent",
          "interpreter_signature",
          "interpreter_signature_unable_consent",
          "patient_rep_signature",
          "witness_signature",
          "witness_signature_unable_consent",
        ];



        setFormData({
          ...response.data,
          ...formattedData,
          ...signatureFields.reduce((acc, field) => {
            if (response.data[field]) {
              acc[field] = response.data[field]; // Directly assign the base64 data from backend
            }
            return acc;
          }, {}),
        });
        setSuccess("success")
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });


  }, [uhid_no]);

  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.CONSENT_FOR_MEDICAL, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.CONSENT_FOR_MEDICAL, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);

  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  const doctorGroups = {
    Cardiologists: [
      "Dr. John Smith",
      "Dr. Emily Johnson",
      "Dr. Michael Brown",
      "Dr. Sarah Davis",
      "Dr. William Wilson",
      "Dr. Linda Martinez"
    ],
    Neurologists: [
      "Dr. Robert Lee",
      "Dr. Patricia Taylor",
      "Dr. James Anderson",
      "Dr. Barbara Thomas",
      "Dr. David Jackson",
      "Dr. Susan White"
    ],
    Orthopedics: [
      "Dr. Richard Garcia",
      "Dr. Jennifer Harris",
      "Dr. Charles Clark",
      "Dr. Jessica Lewis",
      "Dr. Thomas Robinson",
      "Dr. Karen Walker"
    ],
    Dermatologists: [
      "Dr. Nancy Young",
      "Dr. Matthew Hall",
      "Dr. Donna Allen",
      "Dr. Steven King",
      "Dr. Michelle Wright",
      "Dr. George Scott"
    ],
    Gynecologists: [
      "Dr. Sandra Green",
      "Dr. Kevin Adams",
      "Dr. Laura Baker",
      "Dr. Mark Nelson",
      "Dr. Angela Carter",
      "Dr. Brian Mitchell"
    ],
    Pediatricians: [
      "Dr. Melissa Perez",
      "Dr. Eric Roberts",
      "Dr. Deborah Turner",
      "Dr. Jason Phillips",
      "Dr. Rebecca Campbell",
      "Dr. Andrew Parker"
    ],
    AnaesthesiaSpecialists: [
      "Dr. Anil Mehta",
      "Dr. Priya Iyer",
      "Dr. Rakesh Nair",
      "Dr. Neha Sharma",
      "Dr. Vivek Rao",
      "Dr. Aarti Desai"
    ]
  };
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              CONSENT FOR MEDICAL / SURGICAL PROCEDURE
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("CONSENT FOR MEDICAL SURGICAL PROCEDURE", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>

          {/* ✅ Productivity: Step Progress Bar */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* ✅ Productivity: Surgery Templates Quick-Fill */}
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', display: 'block', mb: 1 }}>
              ⚡ QUICK TEMPLATES — Click to auto-fill risks & care plan:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.keys(SURGERY_TEMPLATES).map((proc) => (
                <Tooltip key={proc} title={`Auto-fill risks for ${proc}`}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => applySurgeryTemplate(proc)}
                    startIcon={<AutoFixHighIcon />}
                    sx={{ borderRadius: 3, fontSize: '0.7rem', textTransform: 'none' }}
                  >
                    {proc}
                  </Button>
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="ip_no"
                label="IP No."
                value={formData.ip_no}
                fullWidth
                onChange={handleChange}
                InputProps={{ readOnly: true, }}
                error={false}
                helperText="Auto-generated IP number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="uhid_no"
                label="UHID No."
                onChange={handleChange}
                value={formData.uhid_no}
                InputProps={{ readOnly: true, }}
                error={false}
                helperText="Unique Hospital ID"
              />
            </Grid>
          </Grid>
          <br></br>
          {/* Introductory Paragraph */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            It is the policy of ANNAI ARUL HOSPITAL (P) LTD to inform the patient of the proposed Medical/Surgical Procedure, and you are free to ask your doctors any questions regarding the same.
          </Typography>

          {/* Section 1 */}
          <Box sx={{ marginTop: 2 }}>
            <Typography fontWeight="bold">1. I hereby give consent to the following Operation(s) / Procedure(s) (hereafter referred to as procedure(s)):</Typography>
            <Autocomplete
              freeSolo
              options={operationProcedureOptions}
              value={formData.operation_procedure}
              onInputChange={(event, newInputValue) => {
                setFormData((prev) => ({
                  ...prev,
                  operation_procedure: newInputValue,
                }));
                if (errors.operation_procedure) {
                  setErrors(prev => ({ ...prev, operation_procedure: '' }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={<span>Operation Procedure <span style={{ color: 'red' }}>*</span></span>}
                  multiline
                  rows={2}
                  variant="outlined"
                  sx={{ marginTop: 1 }}
                  fullWidth
                  name="operation_procedure"
                  error={!!errors.operation_procedure}
                  helperText={errors.operation_procedure || "Enter the operation procedure"}
                  onBlur={handleBlur}
                />
              )}
            />
          </Box>

          {/* Section 2 */}
          <Box sx={{ marginTop: 2 }}>
            <Typography fontWeight="bold">2. I have been advised of the major risks and potential complications potential complications involved in the above procedure are listed as follows:</Typography>
            <Autocomplete
              freeSolo
              multiple={false}  // single value
              options={majorRiskOptions}
              value={formData.advised_of_major_risk}
              onChange={(event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  advised_of_major_risk: newValue || '',
                }));
              }}
              renderInput={(params) => (
                <VoiceTextField
                  {...params}
                  label="Advised of Major Risk"
                  multiline
                  rows={3}
                  variant="outlined"
                  sx={{ marginTop: 1 }}
                  name="advised_of_major_risk"
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      advised_of_major_risk: e.target.value,
                    }));
                  }}
                  onVoiceTranscript={(text) => {
                    setFormData(prev => ({
                      ...prev,
                      advised_of_major_risk: (prev.advised_of_major_risk ? prev.advised_of_major_risk + ' ' : '') + text,
                    }));
                  }}
                  error={!!errors.advised_of_major_risk}
                  helperText={errors.advised_of_major_risk || "🎤 Speak or type the major risks"}
                  onBlur={handleBlur}
                />
              )}
            />
          </Box>

          {/* Section 3 */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" component="div" sx={{ lineHeight: 2 }}>
              3. The Care Plan Dr.

              <FormControl variant="standard" sx={{ minWidth: 200, mx: 1, position: 'relative', top: -15 }}

              >
                <InputLabel id="care-plan-dr-label">Select Doctor</InputLabel>
                <Select
                  labelId="care-plan-dr-label"
                  name="care_plan_dr"
                  value={formData.care_plan_dr || ""}
                  onChange={handleNameChange}
                  label="Select Doctor"
                  error={!!errors.care_plan_dr}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Object.entries(doctorGroups).map(([group, doctors]) =>
                    doctors.map((doctor) => (
                      <MenuItem key={doctor} value={doctor}>
                        {doctor}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {' '}in the language they understand explaining the various treatment options, risk

              <Autocomplete
                freeSolo
                options={riskOptions}
                value={formData.care_plan_risks || ""}
                onInputChange={(e, newValue) =>
                  handleChange({ target: { name: "care_plan_risks", value: newValue } })
                }
                sx={underlineInputStyle}
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />

              {' '}like and benefits

              <Autocomplete
                freeSolo
                options={benefitOptions}
                value={formData.care_plan_benefits || ""}
                onInputChange={(e, newValue) =>
                  handleChange({ target: { name: "care_plan_benefits", value: newValue } })
                }
                sx={underlineInputStyle}
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />

              .
            </Typography>
          </Box>
          {/* Section 4 */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            4.  I have been informed about the nature of my disease, the procedure(S) and its prognosis. the alternative to this
            operative treatment and risk of not having the procedure(s) and cost of the treatment have been explained. I have
            also been advised about the anticipated and unanticipated complications. After considering all these i have arrived at
            this decision to undergo the operation.

          </Typography>

          {/* Section 5 */}
          <Box sx={{ marginTop: 2 }}>
            <Typography fontWeight="bold">
              5. I authorize Dr.
            </Typography>
            <FormControl fullWidth variant="standard" size="small">
              <InputLabel id="authorize_dr-label">Select Doctor</InputLabel>
              <Select
                labelId="authorize_dr-label"
                name="authorize_dr"
                value={formData.authorize_dr || ''}
                onChange={handleNameChange}
                label="Select Doctor"
                sx={{ verticalAlign: 'middle' }}
                error={!!errors.authorize_dr}
                onBlur={handleBlur}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.entries(doctorGroups).map(([group, doctors]) =>
                  doctors.map((doctor) => (
                    <MenuItem key={doctor} value={doctor}>
                      {doctor}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Typography variant="body1" sx={{ marginTop: 1 }}>
              and such assistants and associates as may be
              selected by her/his to perform any part of the above procedure upon myself. I have been advised and I agree that any
              member of this team may perform any part of my procedure(s)according to his/her stage of training and ability if in
              the option of above named surgeon, the experience and he capability of the assistant surgeon justifies such a decision.

            </Typography>
          </Box>

          {/* Section 6 */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            6.  As with any procedure am answer that risk such as blood loss infection, heart failure, change in blood pressure,
            anesthetic / allergic reactions, blood clot in let etc. may arise necessitating attention, Therefore, in sedition to
            consenting to the performance of the particular procedure(s), also consent and authorize the rendering of such other
            care and treatment as the surgeon or his designee reasonably believes necessary, Should on or more these and or other
            unforeseeable events occur.

          </Typography>

          {/* Section 7 */}
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="body1">
              7. I consent to the photography or televising of the procedure to be performed for the purpose of advancing medical
              education; or its publication in scientific journals provided my identity is not revealed by the picture descriptions in
              the accompanying texts. I also consent to and authorize the presence of and observation of this procedure by qualified
              observers, as may be authorized by{' '}
              <Autocomplete
                options={authorizeOptions}
                value={formData.authorize_by}
                onChange={(e, newValue) =>
                  handleNameChange({ target: { name: 'authorize_by', value: newValue } })
                }
                sx={{ display: 'inline-block', width: 240 }}
                renderInput={(params) => <TextField {...params} variant="standard" placeholder="e.g., Ethics Committee" error={!!errors.authorize_by} helperText={errors.authorize_by} onBlur={handleBlur} />}
              />
              {' '}and its regulatory laws and agencies.
            </Typography>
          </Box>
          {/* Section 8 */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            8.  I give consent to the administration of blood product transfusion this procedure or immediate post operative period.
            I have been informed that despite careful screening in accordance with national and international regulation, there are
            rare instance of the life threatening infection such as AIDS, hepatitis and other viruses or diseases as yet unknown for
            which screening test do not exist. I also understand that unpredictable reactions may occur which include but are not
            breath. Shock and in rare occasions death.
          </Typography>

          <Box sx={{ marginTop: 2 }}>
            <Typography fontWeight="bold">
              9. The patient and/or family members are informed about the results of the diagnostic tests like{' '}
              <Autocomplete
                options={diagnosisTests}
                value={formData.diagnosis_like}
                onChange={(e, newValue) => handleChange({ target: { name: 'diagnosis_like', value: newValue } })}
                renderInput={(params) => <TextField {...params} variant="standard" />}
                sx={{ display: 'inline-block', width: 200 }}
              />
              {' '}and the diagnosis{' '}
              <Autocomplete
                options={diagnoses}
                value={formData.diagnosis}
                onChange={(e, newValue) => handleChange({ target: { name: 'diagnosis', value: newValue } })}
                renderInput={(params) => <TextField {...params} variant="standard" />}
                sx={{ display: 'inline-block', width: 200 }}
              />
              {' '}in the language they understand by
              <Box component="span" sx={{ display: 'inline-block', minWidth: 150, verticalAlign: 'middle' }}>
                <FormControl variant="standard" size="small" fullWidth>
                  <InputLabel id="diagnosis-dr-label">Dr.</InputLabel>
                  <Select
                    labelId="diagnosis-dr-label"
                    name="diagnosis_dr"
                    value={formData.diagnosis_dr || ''}
                    onChange={handleNameChange}
                    label="Dr."
                    error={!!errors.diagnosis_dr}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.entries(doctorGroups).map(([group, doctors]) =>
                      doctors.map((doctor) => (
                        <MenuItem key={doctor} value={doctor}>
                          {doctor}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>

            </Typography>
          </Box>
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="body1">
              10. The patient and/or family members were explained about any change in the patient's condition like{' '}
              <Autocomplete
                options={conditionOptions}
                value={formData.condition_like}
                onChange={(e, newValue) =>
                  handleChange({ target: { name: 'condition_like', value: newValue } })
                }
                renderInput={(params) => (
                  <TextField {...params} variant="standard" placeholder="e.g., Increased pain" />
                )}
                sx={{ display: 'inline-block', width: 220 }}
              />
              {' '}in a periodic manner in the language they understand by Dr.{'\u00A0'}

              <TextField name='understand_by_dr' variant="standard" onChange={handleNameChange} value={formData.understand_by_dr} error={!!errors.understand_by_dr} helperText={errors.understand_by_dr} onBlur={handleBlur} />
            </Typography>
          </Box>
          {/* Section 11 */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            11. I have been explained that for good long term results. I have to be on regular medical follow up and I undertake to
            abide by the advice given by the doctors.
          </Typography>

          {/* Section 12 */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            12.  I know I must tell the doctor if I have had any other health problems, or any allergies to foods, or medicine,
            Also I must tell the doctor or nurse if I have had anything to eat. taken any drugs, medicine or alcohol since
            midnight.
          </Typography>

          {/* Section 13 */}
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            13.  I know specimens and tissues may be taken from my body during the procedure. I understand that it may be used
            for scientific reasons or in any way the doctor thinks best.

          </Typography>

          {/* Section 14 */}
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="body1" component="span">
              14. All the above details have been explained to me and doubts clarified in the language I understand by Dr.{'\u00A0'}
            </Typography>

            <FormControl

              variant="standard" size="small" fullWidth

            >
              <InputLabel
                id="i-understood-by-dr-label"
                shrink={Boolean(formData.i_understood_by_dr)}  // force label to shrink if value exists
              >
                Select Doctor
              </InputLabel>

              <Select
                labelId="i-understood-by-dr-label"
                name="i_understood_by_dr"
                value={formData.i_understood_by_dr || ''}
                onChange={handleNameChange}
                label={<span>Select Doctor <span style={{ color: 'red' }}>*</span></span>}
                error={!!errors.i_understood_by_dr}
                onBlur={handleBlur}
                sx={{
                  height: 40,
                  fontSize: '0.95rem',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px',
                    paddingLeft: 1,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 240,
                      '& .MuiMenuItem-root': {
                        minHeight: 40,
                        fontSize: '0.95rem',
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.entries(doctorGroups).map(([group, doctors]) =>
                  doctors.map((doctor) => (
                    <MenuItem key={doctor} value={doctor}>
                      {doctor}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Typography variant="body1" component="span">
              {' '}After fully satisfying myself I hereby give my consent for the operation.
            </Typography>
          </Box>


          <Typography variant="h6" sx={{ marginTop: 3 }}>
            Signatures
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                label: "Patient",
                key: "patient_signature",
                key1: "patient_name",
                key2: "patient_signature_date_time"
              },
              {
                label: "Witness",
                key: "witness_signature",
                key1: "witness_name",
                key2: "witness_signature_date_time"
              },
              {
                label: "Doctor/Anaesthetist",
                key: "doctor_anaesthetist_signature",
                key1: "doctor_anaesthetist_name",
                key2: "doctor_anaesthetist_signature_date_time"
              },
              {
                label: "Interpreter",
                key: "interpreter_signature",
                key1: "interpreter_name",
                key2: "interpreter_signature_date_time"
              }

            ].map((person, index) => (
              <Grid item xs={12} sm={6} key={index}>
                {person.label === "Doctor/Anaesthetist" ? (
                  <FormControl fullWidth sx={{ marginTop: 1 }} error={!!errors[person.key1]}>
                    <InputLabel id={`${person.key1}-label`}>
                      <span>Name ({person.label}) <span style={{ color: 'red' }}>*</span></span>
                    </InputLabel>
                    <Select
                      labelId={`${person.key1}-label`}
                      name={person.key1}
                      value={formData[person.key1]}
                      onChange={handleNameChange}
                      label={<span>Name ({person.label}) <span style={{ color: 'red' }}>*</span></span>}
                    >
                      <MenuItem value="">
                        <em>Select Doctor</em>
                      </MenuItem>
                      {doctorGroups["AnaesthesiaSpecialists"]?.map((doctor, idx) => (
                        <MenuItem key={idx} value={doctor}>
                          {doctor}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>{errors[person.key1] || ' '}</FormHelperText>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={<span>Name ({person.label}) <span style={{ color: 'red' }}>*</span></span>}
                    variant="outlined"
                    value={formData[person.key1]}
                    name={person.key1}
                    sx={{ marginTop: 1 }}
                    onChange={handleNameChange}
                    error={!!errors[person.key1]}
                    helperText={errors[person.key1]}
                    onBlur={handleBlur}
                  />
                )}

                <TextField
                  fullWidth
                  label="Date"
                  type="datetime-local"
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                  variant="outlined"
                  sx={{ marginTop: 1 }}
                  value={formData[person.key2]}
                  name={person.key2}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true, }}
                  error={!!errors[person.key2]}
                  helperText={errors[person.key2]}
                  onBlur={handleBlur}
                />

                <Button variant="outlined" color="primary" sx={{ marginTop: 2 }} onClick={() => handleSignatureOpen(person.key)}> Sign Here </Button>
                {formData[person.key] && <img src={`data:image/png;base64,${formData[person.key]}`} alt="Signature" style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }} />}
              </Grid>
            ))}
          </Grid>

          <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
            <DialogTitle>Provide Your Signature</DialogTitle>
            <DialogContent>
              <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} >close</Button>
              <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
              <Button onClick={handleSignatureSave} color="primary">Done</Button>
            </DialogActions>
          </Dialog>

          <Divider sx={{ my: 2 }} />

          {/* Surrogate Consent */}
          <Box sx={{ mt: 4, p: 3, border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
              CONSENT OF PATIENT REPRESENTATIVE / SURROGATE
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              The patient is unable to consent because:
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Autocomplete
                freeSolo
                options={unableConsentReasons}
                value={formData.unable_consent_reason}
                onInputChange={(event, newInputValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    unable_consent_reason: newInputValue,
                  }));
                }}
                sx={{ flex: 1 }}  // Make autocomplete take equal space
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="unable_consent_reason"
                    label="Reason Unable to Consent"
                    variant="outlined"
                    fullWidth  // ensure it fills its container
                  />
                )}
              />

              <Typography variant="body1" sx={{ mx: 2, whiteSpace: 'nowrap' }}>
                And I,
              </Typography>

              <TextField
                name="attender_name"
                value={formData.attender_name}
                label="Attender Name"
                onChange={handleNameChange}
                variant="outlined"
                sx={{ flex: 1 }}
                fullWidth  // ensure it fills its container
              />
            </Box>

            <Typography variant="body1">
              (name/relationship to the patient) therefore consent for the
              procedure with the doctor's designee and hereby consent to this procedure.
            </Typography>
          </Box>
          {/* Signature Section */}
          <Typography variant="h6" sx={{ marginTop: 3 }}>
            Signatures
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                label: "Patient Representative with relationship",
                key: "patient_rep_signature",
                key1: "patient_rep_name",
                key2: "patient_rep_signature_date_time"
              },
              {
                label: "Witness",
                key: "patient_rep_witness_signature",
                key1: "patient_rep_witness_name",
                key2: "patient_rep_witness_date_time"
              },
              {
                label: "Doctor/Anaesthetist",
                key: "patient_rep_doctor_signature",
                key1: "patient_rep_doctor_name",
                key2: "patient_rep_doctor_signature_date_time"
              },
              {
                label: "Interpreter",
                key: "patient_rep_interpreter_signature",
                key1: "patient_rep_interpreter_name",
                key2: "patient_rep_interpreter_signature_date_time"
              }

            ].map((person, index) => (
              <Grid item xs={12} sm={6} key={index}>
                {person.label === "Doctor/Anaesthetist" ? (
                  <FormControl fullWidth sx={{ marginTop: 1 }} error={!!errors[person.key1]}>
                    <InputLabel id={`rep-${person.key1}-label`}>
                      <span>Name ({person.label}) <span style={{ color: 'red' }}>*</span></span>
                    </InputLabel>
                    <Select
                      labelId={`rep-${person.key1}-label`}
                      name={person.key1}
                      value={formData[person.key1]}
                      onChange={handleNameChange}
                      label={<span>Name ({person.label}) <span style={{ color: 'red' }}>*</span></span>}
                    >
                      <MenuItem value="">
                        <em>Select Doctor</em>
                      </MenuItem>
                      {doctorGroups["AnaesthesiaSpecialists"]?.map((doctor, idx) => (
                        <MenuItem key={idx} value={doctor}>
                          {doctor}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>{errors[person.key1] || ' '}</FormHelperText>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={<span>Name ({person.label}) <span style={{ color: 'red' }}>*</span></span>}
                    variant="outlined"
                    value={formData[person.key1]}
                    name={person.key1}
                    sx={{ marginTop: 1 }}
                    onChange={handleNameChange}
                    error={!!errors[person.key1]}
                    helperText={errors[person.key1]}
                    onBlur={handleBlur}
                  />
                )}

                <TextField
                  fullWidth
                  label="Date"
                  type="datetime-local"
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                  variant="outlined"
                  sx={{ marginTop: 1 }}
                  value={formData[person.key2]}
                  name={person.key2}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true, }}
                  error={!!errors[person.key2]}
                  helperText={errors[person.key2]}
                  onBlur={handleBlur}
                />
                <Button variant="outlined" color="primary" sx={{ marginTop: 2 }} onClick={() => handleSignatureOpen(person.key)}> Sign Here </Button>
                {formData[person.key] && <img src={`data:image/png;base64,${formData[person.key]}`} alt="Signature" style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }} />}
              </Grid>
            ))}
          </Grid>

          <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
            <DialogTitle>Provide Your Signature</DialogTitle>
            <DialogContent>
              <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} >close</Button>
              <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
              <Button onClick={handleSignatureSave} color="primary">Done</Button>
            </DialogActions>
          </Dialog>

          <Divider sx={{ my: 2 }} />
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.CONSENT_FOR_MEDICAL}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(draftData)}
                variant="outlined"
                size="large"
                isUpdate={success === "success"}
              />
              {/* Submit Button */}
              <Button type="submit" variant="contained" color="primary" sx={{ marginTop: 3 }} fullWidth onClick={ConsentFormSubmit}>
                {success === "success" ? 'Update ' : 'Submit'}
              </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          </Grid>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ConsentForm;
